import { FileText, Image as ImageIcon, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import type { SalesLeadActivityAttachment } from '@/models/salesLead'

const IMAGE_EXT = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp'])

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

interface ActivityAttachmentsProps {
  attachments: SalesLeadActivityAttachment[]
  /** Shows a remove button on each file. */
  onRemove?: (id: string) => void
}

export function ActivityAttachments({ attachments, onRemove }: ActivityAttachmentsProps) {
  if (attachments.length === 0) return null
  return (
    <div className="mt-3 flex flex-wrap gap-2">
      {attachments.map((att) => {
        const ext = att.fileExtension.toLowerCase()
        const Icon = IMAGE_EXT.has(ext) ? ImageIcon : FileText
        const content = (
          <>
            <Icon className="text-muted-foreground size-3.5 shrink-0" />
            <span className="max-w-[16rem] truncate font-medium">{att.fileName}</span>
            <span className="text-muted-foreground text-[10px]">{formatSize(att.fileSize)}</span>
          </>
        )
        const className =
          'bg-muted/40 hover:bg-muted flex items-center gap-2 rounded-md border px-2 py-1.5 text-xs transition-colors'
        if (onRemove) {
          return (
            <div key={att.id} className={className} title={att.fileName}>
              {att.fileUrl ? (
                <a
                  href={att.fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2"
                >
                  {content}
                </a>
              ) : (
                content
              )}
              <Button
                type="button"
                variant="iconOnly"
                size="icon-sm"
                className="size-5"
                onClick={() => onRemove(att.id)}
                aria-label={`Remove ${att.fileName}`}
              >
                <X className="size-3" />
              </Button>
            </div>
          )
        }
        if (att.fileUrl) {
          return (
            <a
              key={att.id}
              href={att.fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={className}
              title={att.fileName}
            >
              {content}
            </a>
          )
        }
        return (
          <div key={att.id} className={className} title={att.fileName}>
            {content}
          </div>
        )
      })}
    </div>
  )
}
